import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { RoommateCard } from "@/components/RoommateCard";
import { PROPERTY_TYPES, AMENITIES, GENDER_PREF } from "@/data/constants";
import { useInstitutions } from "@/lib/useInstitutions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { GraduationCap, Search, X, Users, Plus, SlidersHorizontal } from "lucide-react";
import { SaveSearchDialog } from "@/components/SaveSearchDialog";

const HERO = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Students() {
  const [housing, setHousing] = useState([]);
  const [roommates, setRoommates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ institution: "", property_type: "", max_distance: "", town: "", max_rent: "", gender_pref: "" });
  const [amen, setAmen] = useState([]);
  const [sort, setSort] = useState("");
  const [rmType, setRmType] = useState("");
  const [applied, setApplied] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const lp = { category: "student" };
      if (f.institution) lp.institution = f.institution;
      if (f.property_type) lp.property_type = f.property_type;
      if (f.max_distance) lp.max_distance = f.max_distance;
      if (f.town) lp.town = f.town;
      if (f.gender_pref) lp.gender_pref = f.gender_pref;
      if (amen.length) lp.amenities = amen.join(",");
      if (sort) lp.sort = sort;
      const rp = {};
      if (f.institution) rp.institution = f.institution;
      if (f.town) rp.town = f.town;
      if (f.max_rent) rp.max_rent = f.max_rent;
      if (f.gender_pref) rp.gender_pref = f.gender_pref;
      if (amen.length) rp.amenities = amen.join(",");
      if (rmType) rp.post_type = rmType;
      const [h, r] = await Promise.all([
        api.get("/listings", { params: lp }),
        api.get("/roommates", { params: rp }),
      ]);
      setHousing(h.data);
      setRoommates(r.data);
    } finally {
      setLoading(false);
    }
  }, [applied]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const { institutions } = useInstitutions();
  const toggleAmen = (a) => setAmen((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]));
  const apply = () => setApplied((n) => n + 1);
  const clear = () => { setF({ institution: "", property_type: "", max_distance: "", town: "", max_rent: "", gender_pref: "" }); setAmen([]); setApplied((n) => n + 1); };

  const activeCount = Object.values(f).filter((v) => v !== "").length + amen.length + (sort ? 1 : 0) + (rmType ? 1 : 0);

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={HERO} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-[#2C3830]/80" /></div>
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <span className="label-caps text-[#D4A373]">For students & parents</span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-light leading-[1.05] tracking-tight text-white sm:text-5xl">Student housing, near your campus.</h1>
          <p className="mt-4 max-w-xl text-base text-white/75">Find hostels, bedsitters and shared spaces close to your institution — or find a roommate to share the rent.</p>
          <Link to="/roommates/new"><Button data-testid="post-roommate-btn" className="mt-6 rounded-full bg-[#D4A373] text-white hover:bg-[#c39364]"><Plus className="mr-1.5 h-4 w-4" /> Post "Roommate wanted"</Button></Link>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border/60 bg-[#F7F5F0]">
        <div className="mx-auto max-w-7xl px-5 py-5 lg:px-8">
          {/* Mobile: collapsible toggle */}
          <div className="md:hidden">
            <Button
              data-testid="filter-toggle"
              onClick={() => setOpen((o) => !o)}
              variant="outline"
              className="w-full justify-center rounded-full border-[#2C3830]/20 bg-white text-[#2C3830]"
            >
              <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
              {activeCount > 0 && (
                <span className="ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#2C3830] px-1 text-xs text-white">
                  {activeCount}
                </span>
              )}
            </Button>
          </div>

          {/* Filter body — collapsible on mobile, always visible md+ */}
          <div className={`${open ? "mt-3 block" : "hidden"} md:mt-0 md:block`}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <div className="col-span-2 lg:col-span-2">
              <Select value={f.institution || "any"} onValueChange={(v) => set("institution", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-institution" className="rounded-full bg-white"><SelectValue placeholder="Institution" /></SelectTrigger>
                <SelectContent><SelectItem value="any">All institutions</SelectItem>{institutions.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Select value={f.property_type || "any"} onValueChange={(v) => set("property_type", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="filter-housing-type" className="rounded-full bg-white"><SelectValue placeholder="Housing type" /></SelectTrigger>
              <SelectContent><SelectItem value="any">All types</SelectItem>{PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="filter-distance" type="number" value={f.max_distance} onChange={(e) => set("max_distance", e.target.value)} placeholder="Max km to campus" className="rounded-full bg-white" />
            <Input data-testid="filter-max-rent" type="number" value={f.max_rent} onChange={(e) => set("max_rent", e.target.value)} placeholder="Max rent" className="rounded-full bg-white" />
            <Select value={sort || "recent"} onValueChange={(v) => setSort(v === "recent" ? "" : v)}>
              <SelectTrigger data-testid="filter-sort" className="rounded-full bg-white"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent><SelectItem value="recent">Most recent</SelectItem><SelectItem value="distance">Nearest to campus</SelectItem></SelectContent>
            </Select>
            <Select value={rmType || "all"} onValueChange={(v) => setRmType(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-rmtype" className="rounded-full bg-white"><SelectValue placeholder="Roommate type" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All roommates</SelectItem><SelectItem value="offering">Offering a space</SelectItem><SelectItem value="seeking">Looking for a room</SelectItem></SelectContent>
            </Select>
            <Select value={f.gender_pref || "any"} onValueChange={(v) => set("gender_pref", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="filter-gender" className="rounded-full bg-white"><SelectValue placeholder="Gender" /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any gender</SelectItem>{GENDER_PREF.filter((g) => g.value !== "any").map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {AMENITIES.map((a) => (
              <button key={a} data-testid={`amenity-${a}`} onClick={() => toggleAmen(a)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${amen.includes(a) ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border bg-white text-[#6B7280] hover:border-[#2C3830]"}`}>{a}</button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button data-testid="apply-filters" onClick={apply} className="rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Apply</Button>
            <SaveSearchDialog
              source="students"
              params={{
                category: "student", institution: f.institution, property_type: f.property_type,
                max_distance: f.max_distance ? Number(f.max_distance) : "",
                town: f.town, gender_pref: f.gender_pref, amenities: amen,
              }}
            />
            <Button data-testid="clear-filters" onClick={clear} variant="ghost" className="rounded-full text-[#6B7280]"><X className="mr-1 h-4 w-4" /> Clear</Button>
          </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
        <Tabs defaultValue="housing">
          <TabsList className="rounded-full bg-white p-1">
            <TabsTrigger data-testid="tab-housing" value="housing" className="rounded-full"><GraduationCap className="mr-1.5 h-4 w-4" /> Student Housing ({housing.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-roommates" value="roommates" className="rounded-full"><Users className="mr-1.5 h-4 w-4" /> Roommates ({roommates.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="housing" className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}</div>
            ) : housing.length === 0 ? (
              <div data-testid="housing-empty" className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border py-20 text-center"><GraduationCap className="h-10 w-10 text-[#D4A373]" /><p className="font-display text-xl text-[#2C3830]">No student housing yet</p><p className="text-sm text-[#6B7280]">Try widening your filters.</p></div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{housing.map((l, i) => <PropertyCard key={l.id} listing={l} index={i} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="roommates" className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}</div>
            ) : roommates.length === 0 ? (
              <div data-testid="roommates-empty" className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border py-20 text-center"><Users className="h-10 w-10 text-[#D4A373]" /><p className="font-display text-xl text-[#2C3830]">No roommate posts yet</p><Link to="/roommates/new"><Button className="mt-2 rounded-full bg-[#2C3830] text-white"><Plus className="mr-1.5 h-4 w-4" /> Post one</Button></Link></div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{roommates.map((p, i) => <RoommateCard key={p.id} post={p} index={i} />)}</div>
            )}
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
